import React, { useId, useState } from "react";
import { AccordionItem } from "./AccordionItem";
import { VariantProps, cva } from "class-variance-authority";

export type AccordionIcon = "chevron" | "plus-minus";

const accordion = cva("divide-x-0 divide-y divide-solid", {
  variants: {
    dividerColor: {
      gray: "divide-gray-200",
      primary: "divide-primary-200",
    },
  },
  defaultVariants: { dividerColor: "primary" },
});

export interface AccordionProps extends VariantProps<typeof accordion> {
  defaultExpanded?: number[];
  entries: {
    title: React.ReactNode | string;
    content: React.ReactNode | string;
  }[];
  icon?: AccordionIcon;
  onChange?: (value: number) => void;
  type?: "multiple" | "single";
}

export const Accordion = ({
  entries,
  onChange,
  dividerColor,
  icon = "plus-minus",
  defaultExpanded,
  type = "multiple",
}: AccordionProps) => {
  const [expanded, setExpanded] = useState<number[]>(defaultExpanded ?? []);
  const id = useId();

  const handleChange = (index: number) => {
    if (expanded.includes(index)) {
      setExpanded((prev) => prev.filter((item) => item !== index));
    } else {
      type == "single"
        ? setExpanded([index])
        : setExpanded((prev) => [...prev, index]);
    }
    onChange && onChange(index);
  };

  return (
    <ul className={accordion({ dividerColor })}>
      {entries.map((entry, index) => (
        <AccordionItem
          id={`${id}${index}`}
          key={index}
          {...entry}
          title={entry.title}
          content={entry.content}
          open={expanded.includes(index)}
          onChange={() => handleChange(index)}
          icon={icon}
        />
      ))}
    </ul>
  );
};
